/**
 * ============================================================================
 * NewRiskDialog — modal for opening a new quality risk (Phase 7, ICH Q9).
 * Owner: Phase 7 — risk-svc.
 *
 * Posts to the PG-backed POST /api/v1/risks (drafts/61 F0-8), which inserts a
 * row into risk.risk_entries at status OPEN with a RISK-YYYY-NNNN number. The
 * RPN (severity × occurrence × detectability) and the resulting risk band are
 * computed server-side; this dialog shows a live preview so the assessor sees
 * the band before submitting. On success the register refetches.
 *
 * URS rows: RSK-001 (register), RSK-004 (RPN bands).
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import { buildHeaders, apiUrl } from '../../api/client';
import styles from './RiskRegister.module.css';
import type { RiskCategory, RiskLevel, TreatmentKind } from './useRisksQuery';

export interface NewRiskDialogProps {
  onClose: () => void;
  onCreated: (riskId: string) => void;
}

interface FormState {
  title: string;
  description: string;
  riskCategory: RiskCategory;
  processScope: string;
  severity: number;
  occurrence: number;
  detectability: number;
  treatmentKind: TreatmentKind;
}

const INITIAL: FormState = {
  title: '',
  description: '',
  riskCategory: 'PRODUCT_QUALITY',
  processScope: '',
  severity: 3,
  occurrence: 3,
  detectability: 3,
  treatmentKind: 'MITIGATE',
};

const CATEGORIES: RiskCategory[] = [
  'PRODUCT_QUALITY',
  'PATIENT_SAFETY',
  'DATA_INTEGRITY',
  'REGULATORY',
  'SUPPLY',
  'EQUIPMENT',
  'CYBER',
  'OTHER',
];
const TREATMENTS: TreatmentKind[] = ['MITIGATE', 'TRANSFER', 'ACCEPT', 'AVOID'];
const SCORES = [1, 2, 3, 4, 5];

// Mirrors the backend banding (drafts/61 F0-8) for a live preview.
function bandFor(rpn: number): RiskLevel {
  if (rpn >= 48) return 'CRITICAL';
  if (rpn >= 24) return 'HIGH';
  if (rpn >= 12) return 'MEDIUM';
  return 'LOW';
}

const LEVEL_CLASS: Record<RiskLevel, string> = {
  CRITICAL: styles.levelCritical,
  HIGH: styles.levelHigh,
  MEDIUM: styles.levelMedium,
  LOW: styles.levelLow,
};

export function NewRiskDialog({ onClose, onCreated }: NewRiskDialogProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>();

  function update<K extends keyof FormState>(key: K, v: FormState[K]) {
    setForm((p) => ({ ...p, [key]: v }));
  }

  const rpn = useMemo(
    () => form.severity * form.occurrence * form.detectability,
    [form.severity, form.occurrence, form.detectability],
  );
  const band = bandFor(rpn);

  async function submit() {
    if (!form.title.trim() || !form.description.trim()) {
      setErrorMsg(
        t('risk.create.missingRequired', {
          defaultValue: 'A risk title and a description are required.',
        }),
      );
      return;
    }
    setSubmitting(true);
    setErrorMsg(undefined);
    try {
      const r = await fetch(apiUrl('/api/v1/risks'), {
        method: 'POST',
        headers: buildHeaders({ json: true }),
        body: JSON.stringify({
          title: form.title.trim(),
          description: form.description.trim(),
          riskCategory: form.riskCategory,
          processScope: form.processScope.trim() || 'general',
          severity: form.severity,
          occurrence: form.occurrence,
          detectability: form.detectability,
          treatmentKind: form.treatmentKind,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErrorMsg(
          (body as { detail?: string }).detail ??
            t('risk.create.failed', { defaultValue: 'Open risk failed.' }),
        );
        return;
      }
      const body = (await r.json()) as { id?: string; riskId?: string };
      onCreated(body.riskId ?? body.id ?? '');
    } catch (e) {
      setErrorMsg(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.dialogShade} role="dialog" aria-modal="true">
      <div className={styles.dialogBox}>
        <h2 className={styles.dialogTitle}>
          {t('risk.create.title', { defaultValue: 'Open a quality risk' })}
        </h2>
        <p className={styles.dialogSubtitle}>
          {t('risk.create.subtitle', {
            defaultValue:
              'Registers a new risk at OPEN (ICH Q9). Score severity, occurrence, and detectability to compute the RPN band.',
          })}
        </p>

        <div className={styles.formField}>
          <label htmlFor="risk-title">{t('risk.create.titleLabel', { defaultValue: 'Title' })}</label>
          <input
            id="risk-title"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder={t('risk.create.titlePlaceholder', {
              defaultValue: 'Short summary, e.g. "Cold-chain excursion during transit"',
            })}
          />
        </div>

        <div className={styles.formField}>
          <label htmlFor="risk-desc">
            {t('risk.create.descLabel', { defaultValue: 'Description / hazard' })}
          </label>
          <textarea
            id="risk-desc"
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder={t('risk.create.descPlaceholder', {
              defaultValue:
                'Describe the hazard, the potential harm, and the affected process or product.',
            })}
          />
        </div>

        <div className={styles.twoCol}>
          <div className={styles.formField}>
            <label htmlFor="risk-cat">{t('risk.create.catLabel', { defaultValue: 'Category' })}</label>
            <select
              id="risk-cat"
              value={form.riskCategory}
              onChange={(e) => update('riskCategory', e.target.value as RiskCategory)}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.formField}>
            <label htmlFor="risk-scope">
              {t('risk.create.scopeLabel', { defaultValue: 'Process scope' })}
            </label>
            <input
              id="risk-scope"
              value={form.processScope}
              onChange={(e) => update('processScope', e.target.value)}
              placeholder={t('risk.create.scopePlaceholder', { defaultValue: 'e.g. Sterile filling' })}
            />
          </div>
        </div>

        <div className={styles.threeCol}>
          <div className={styles.formField}>
            <label htmlFor="risk-sev">{t('risk.create.sevLabel', { defaultValue: 'Severity (S)' })}</label>
            <select
              id="risk-sev"
              value={form.severity}
              onChange={(e) => update('severity', Number(e.target.value))}
            >
              {SCORES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.formField}>
            <label htmlFor="risk-occ">{t('risk.create.occLabel', { defaultValue: 'Occurrence (O)' })}</label>
            <select
              id="risk-occ"
              value={form.occurrence}
              onChange={(e) => update('occurrence', Number(e.target.value))}
            >
              {SCORES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.formField}>
            <label htmlFor="risk-det">{t('risk.create.detLabel', { defaultValue: 'Detectability (D)' })}</label>
            <select
              id="risk-det"
              value={form.detectability}
              onChange={(e) => update('detectability', Number(e.target.value))}
            >
              {SCORES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.rpnPreview}>
          <span className={styles.rpnPreviewLabel}>
            {t('risk.create.rpnLabel', { defaultValue: 'Computed RPN' })}
          </span>
          <span className={styles.rpnPreviewValue}>{rpn}</span>
          <span className={`${styles.levelChip} ${LEVEL_CLASS[band]}`}>
            <span className={styles.dot} aria-hidden />
            {t(`risk.level.${band}`, { defaultValue: band })}
          </span>
        </div>

        <div className={styles.formField}>
          <label htmlFor="risk-treat">
            {t('risk.create.treatLabel', { defaultValue: 'Treatment strategy' })}
          </label>
          <select
            id="risk-treat"
            value={form.treatmentKind}
            onChange={(e) => update('treatmentKind', e.target.value as TreatmentKind)}
          >
            {TREATMENTS.map((tk) => (
              <option key={tk} value={tk}>
                {tk}
              </option>
            ))}
          </select>
        </div>

        {errorMsg && (
          <div className={styles.errorBanner} role="alert">
            {errorMsg}
          </div>
        )}

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>
            {submitting
              ? t('common.submitting', { defaultValue: 'Submitting…' })
              : t('risk.create.submit', { defaultValue: 'Open risk' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
